import React from 'react';

const playBadge = (color) => (
  <span className={`h-7 w-7 shrink-0 rounded-full flex items-center justify-center text-[11px] text-white ${color}`}>
    ▶
  </span>
);
const opensOutside = <span aria-hidden="true">↗</span>;

// One row per content item, the Action Songs / Stories screens' resource rows. Labels and actions
// match CourseChapterPanel's cards, so a button is named the same on every screen.
const RESOURCE_ROWS = {
  pdf: {
    icon: '📄',
    label: 'Worksheet',
    action: 'Open PDF',
    row: 'bg-rose-50',
    button: 'bg-white border-2 border-rose-200 text-blue-700 hover:bg-rose-50',
    trailing: opensOutside,
  },
  audio: {
    icon: '🎧',
    label: 'Audio',
    action: 'Play Audio',
    row: 'bg-green-50',
    button: 'bg-white border-2 border-green-300 text-green-700 hover:bg-green-50',
    leading: playBadge('bg-green-500'),
  },
  video: {
    icon: '🎥',
    label: 'Video',
    action: 'Watch Video',
    row: 'bg-blue-50',
    button: 'bg-white border-2 border-blue-200 text-blue-700 hover:bg-blue-50',
    leading: playBadge('bg-blue-500'),
  },
  quiz: {
    icon: '❓',
    label: 'Quiz',
    action: 'Start Quiz',
    row: 'bg-violet-50',
    button: 'bg-violet-600 border-2 border-violet-600 text-white hover:bg-violet-700',
  },
  link: {
    icon: '🎮',
    label: 'Game',
    action: 'Play Game',
    row: 'bg-amber-50',
    button: 'bg-white border-2 border-amber-300 text-amber-700 hover:bg-amber-50',
    trailing: opensOutside,
  },
  image: {
    icon: '🖼️',
    label: 'Picture',
    action: 'View Picture',
    row: 'bg-sky-50',
    button: 'bg-white border-2 border-sky-200 text-sky-700 hover:bg-sky-50',
  },
  text: {
    icon: '📝',
    label: 'Reading',
    action: 'Read',
    row: 'bg-slate-50',
    button: 'bg-white border-2 border-slate-300 text-slate-700 hover:bg-slate-100',
  },
  task: {
    icon: '✅',
    label: 'Task',
    action: 'Open Task',
    row: 'bg-teal-50',
    button: 'bg-white border-2 border-teal-200 text-teal-700 hover:bg-teal-50',
  },
};

/**
 * The selected song's / story's content items as stacked rows (icon and name on the left, button on
 * the right), under its title, ISF coins and description. `heading` and `rowPrefix` default to the
 * lesson's title; a numbered story list passes "Story 6 - Title" / "Story 6".
 * `onOpenItem(item)` opens one (CourseContentView).
 */
export default function ResourceRowsPanel({ chapter, heading = chapter.title, rowPrefix = chapter.title, onOpenItem }) {
  // Two items of the same type are told apart by their own titles.
  const typeCounts = chapter.items.reduce((counts, item) => ({ ...counts, [item.type]: (counts[item.type] || 0) + 1 }), {});

  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
      <div className={`flex items-center justify-between gap-3 ${chapter.description ? 'mb-4' : 'mb-5'}`}>
        <h2 className="text-3xl font-extrabold text-slate-900">{heading}</h2>
        {chapter.coins > 0 && (
          <span className="shrink-0 flex items-center gap-1.5 bg-amber-100 text-amber-700 font-extrabold rounded-full px-4 py-1.5 text-sm">
            🪙 {chapter.coins} ISF Coins
          </span>
        )}
      </div>

      {chapter.description && (
        <p className="bg-blue-50 text-blue-800 font-semibold text-[15px] rounded-xl px-4 py-3.5 mb-6">{chapter.description}</p>
      )}

      <div className="flex flex-col gap-3">
        {chapter.items.map((item) => {
          const resource = RESOURCE_ROWS[item.type] || RESOURCE_ROWS.text;
          const name = typeCounts[item.type] > 1 ? item.title : resource.label;
          return (
            <div
              key={item.id}
              className={`rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 ${resource.row}`}
            >
              <div className="flex-1 min-w-0 flex items-center gap-4">
                <span className="w-14 shrink-0 text-center text-5xl" aria-hidden="true">
                  {resource.icon}
                </span>
                <span className="font-extrabold text-slate-800 leading-tight break-words min-w-0">
                  {rowPrefix} {name}
                </span>
              </div>
              <button
                onClick={() => onOpenItem(item)}
                aria-label={`${resource.action}: ${rowPrefix} ${name}`}
                className={`w-full sm:w-48 shrink-0 flex items-center justify-center gap-2 rounded-xl px-3 py-3 text-sm font-extrabold whitespace-nowrap transition-colors ${resource.button}`}
              >
                {resource.leading}
                {resource.action}
                {resource.trailing}
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}
